import { useEffect, useMemo, useState } from 'react';
import { apiRequest, apiRoutes } from '../../../api';
import { Field, Select, TextInput } from './WizardControls';

function latestRevision(template) {
  return (template.revisions || [])[0];
}

function formatAmenityValue(amenity, value) {
  if (value == null || value === '') return '';
  if (amenity.value_type === 'boolean') return value ? 'دارد' : 'ندارد';
  const options = amenity.categorical_options || {};
  return options[value] || value;
}

const SECTION_LABELS = { PHASE: 'فاز', BLOCK: 'بلوک', BUILDING: 'ساختمان' };
const SECTION_PARENT_TYPES = { PHASE: [], BLOCK: ['PHASE'], BUILDING: ['BLOCK'] };

function sectionPath(section, sections) {
  const parent = sections.find((item) => item.id === section.parent);
  return parent ? `${sectionPath(parent, sections)} / ${section.name}` : section.name;
}

function SectionTree({ sections, busy, onDeleteSection }) {
  const childrenOf = (parentId) => sections.filter((section) => (section.parent || null) === parentId);
  const renderBranch = (section, depth = 0) => (
    <div className="dev-section-tree-branch" key={section.id}>
      <div className="dev-section-tree-row" style={{ '--tree-depth': depth }}>
        <span className="dev-section-tree-marker" />
        <span>{section.name}</span>
        <small>{SECTION_LABELS[section.type] || section.type}</small>
        <button type="button" disabled={busy} onClick={() => onDeleteSection(section.id)} aria-label={`حذف ${section.name}`}>×</button>
      </div>
      {childrenOf(section.id).map((child) => renderBranch(child, depth + 1))}
    </div>
  );

  return (
    <div className="dev-section-tree">
      {childrenOf(null).map((section) => renderBranch(section))}
      {!sections.length && <small className="dev-muted">هنوز ساختاری ثبت نشده است.</small>}
    </div>
  );
}

function defaultUnitNumbers({ floor, count }, existingUnits = []) {
  const total = Number(count);
  if (!Number.isInteger(total) || total < 1) return '';
  const floorPrefix = floor === '' || floor == null ? '' : String(floor);
  const existingNumbers = new Set(existingUnits.map((unit) => String(unit.unit_number)));
  const existingSequences = existingUnits
    .map((unit) => String(unit.unit_number))
    .filter((number) => !floorPrefix || number.startsWith(floorPrefix))
    .map((number) => Number(floorPrefix ? number.slice(floorPrefix.length) : number))
    .filter(Number.isInteger);
  let sequence = Math.max(0, ...existingSequences) + 1;
  const numbers = [];
  while (numbers.length < total) {
    const candidateSequence = String(sequence).padStart(2, '0');
    const candidate = floorPrefix ? `${floorPrefix}${candidateSequence}` : candidateSequence;
    if (!existingNumbers.has(candidate)) numbers.push(candidate);
    sequence += 1;
  }
  return numbers.join(', ');
}

function AmenityInput({ amenity, value, onChange }) {
  if (amenity.value_type === 'boolean') {
    return (
      <button
        type="button"
        className={`dev-amenity-chip ${value ? 'active' : ''}`}
        onClick={() => onChange(!value)}
      >
        {amenity.name}
      </button>
    );
  }

  if (amenity.value_type === 'categorical') {
    const options = Object.entries(amenity.categorical_options || {});
    return (
      <Field label={amenity.name}>
        <Select value={value ?? ''} onChange={(next) => onChange(next === '' ? null : Number(next))}>
          <option value="">انتخاب</option>
          {options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </Select>
      </Field>
    );
  }

  if (amenity.value_type === 'numerical') {
    return (
      <Field label={amenity.name}>
        <TextInput type="number" value={value ?? ''} onChange={(next) => onChange(next === '' ? null : Number(next))} />
      </Field>
    );
  }

  return (
    <Field label={amenity.name}>
      <TextInput value={value ?? ''} onChange={onChange} />
    </Field>
  );
}

function PlanUpload({ busy, revision, onUploadPlan }) {
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('plan', file);
    await onUploadPlan(revision.id, fd);
    event.target.value = '';
  };

  return (
    <label className="dev-plan-upload">
      <input type="file" accept="image/*" disabled={busy} onChange={upload} />
      <span>{revision.plan_details ? 'تعویض پلان' : 'پلان'}</span>
    </label>
  );
}

export default function ProjectStructureStep({
  busy,
  projectBundle,
  sectionForm,
  selectedProject,
  statusLabels,
  templateForm,
  unitBatch,
  onCreateSection,
  onCreateTemplate,
  onDeleteSection,
  onDeleteTemplate,
  onDeleteUnit,
  onPublishRevision,
  onUploadPlan,
  onSectionFormChange,
  onTemplateFormChange,
  onBulkCreateUnits,
  onUnitBatchChange
}) {
  const [amenities, setAmenities] = useState([]);
  const [amenityValues, setAmenityValues] = useState({});

  const publishedRevisions = useMemo(
    () => projectBundle.templates.flatMap((template) =>
      (template.revisions || [])
        .filter((revision) => revision.status === 'PUBLISHED')
        .map((revision) => ({ ...revision, templateName: template.name, templateCode: template.code }))
    ),
    [projectBundle.templates]
  );

  useEffect(() => {
    apiRequest(apiRoutes.properties.amenitiesByPropertyTypeName('پیش فروش'))
      .then((response) => response.ok ? response.json() : [])
      .then((data) => setAmenities(Array.isArray(data) ? data : data.results || []))
      .catch(() => setAmenities([]));
  }, []);

  useEffect(() => {
    const existingUnits = projectBundle.units.filter((unit) => (
      String(unit.section || '') === String(unitBatch.section || '') &&
      String(unit.floor ?? '') === String(unitBatch.floor ?? '')
    ));
    const numbers = defaultUnitNumbers(unitBatch, existingUnits);
    onUnitBatchChange({ ...unitBatch, unit_numbers: numbers });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitBatch.floor, unitBatch.count, unitBatch.section, projectBundle.units]);

  const setAmenityValue = (amenityId, value) => {
    setAmenityValues((current) => ({ ...current, [amenityId]: value }));
  };

  const submitTemplate = async (event) => {
    event.preventDefault();
    const selectedAmenities = amenities
      .map((amenity) => ({ amenity: amenity.id, value: amenityValues[amenity.id], notes: '' }))
      .filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
    await onCreateTemplate(event, selectedAmenities);
    setAmenityValues({});
  };

  return (
    <div className="dev-merged-structure-step">
      <form className="dev-panel dev-merged-card" onSubmit={onCreateSection}>
        <div className="dev-merged-head">
          <h2>ساختار پروژه</h2>
          <button className="dev-btn secondary mini" disabled={!selectedProject || busy || (sectionForm.type !== 'PHASE' && !sectionForm.parent)}>افزودن</button>
        </div>
        <div className="dev-form-stack compact">
          <Field label="نام بخش"><TextInput value={sectionForm.name} onChange={(value) => onSectionFormChange({ ...sectionForm, name: value })} /></Field>
          <Field label="نوع بخش">
            <Select value={sectionForm.type} onChange={(value) => onSectionFormChange({ ...sectionForm, type: value, parent: '' })}>
              <option value="PHASE">فاز</option>
              <option value="BUILDING">ساختمان</option>
              <option value="BLOCK">بلوک</option>
            </Select>
          </Field>
          {sectionForm.type !== 'PHASE' && (
            <Field label="والد">
              <Select value={sectionForm.parent} onChange={(value) => onSectionFormChange({ ...sectionForm, parent: value })}>
                <option value="">انتخاب {sectionForm.type === 'BLOCK' ? 'فاز' : 'بلوک'}</option>
                {projectBundle.sections
                  .filter((section) => SECTION_PARENT_TYPES[sectionForm.type].includes(section.type))
                  .map((section) => <option key={section.id} value={section.id}>{section.name}</option>)}
              </Select>
            </Field>
          )}
        </div>
        <div className="dev-tags compact">
          <SectionTree sections={projectBundle.sections} busy={busy} onDeleteSection={onDeleteSection} />
        </div>
      </form>

      <form className="dev-panel dev-merged-card dev-template-card" onSubmit={submitTemplate}>
        <div className="dev-merged-head">
          <h2>تیپ واحدها</h2>
          <button className="dev-btn primary mini" disabled={!selectedProject || busy}>ایجاد تیپ</button>
        </div>
        <div className="dev-form-grid">
          <Field label="نام تیپ"><TextInput value={templateForm.name} onChange={(value) => onTemplateFormChange({ ...templateForm, name: value })} /></Field>
          <Field label="مساحت"><TextInput type="number" value={templateForm.area} onChange={(value) => onTemplateFormChange({ ...templateForm, area: value })} /></Field>
        </div>
        <div className="dev-amenities-picker">
          {amenities.map((amenity) => (
            <AmenityInput
              key={amenity.id}
              amenity={amenity}
              value={amenityValues[amenity.id]}
              onChange={(value) => setAmenityValue(amenity.id, value)}
            />
          ))}
          {!amenities.length && <small className="dev-muted">برای نوع پیش فروش هنوز amenity تعریف نشده است.</small>}
        </div>
        <div className="dev-list compact">
          {projectBundle.templates.map((template) => {
            const revision = latestRevision(template);
            return (
              <div className="dev-list-row static" key={template.id}>
                <span>{template.name}</span>
                <small>{revision?.area ? `${revision.area} متر` : template.code}</small>
                {revision && (
                  <div className="dev-template-actions">
                    {revision.status === 'DRAFT' && <PlanUpload busy={busy} revision={revision} onUploadPlan={onUploadPlan} />}
                    {revision.plan_details?.file_url && <a href={revision.plan_details.file_url} target="_blank" rel="noreferrer">مشاهده پلان</a>}
                    <button type="button" className="dev-btn mini" disabled={revision.status !== 'DRAFT' || busy} onClick={() => onPublishRevision(revision.id)}>
                      {revision.status === 'DRAFT' ? 'انتشار' : statusLabels[revision.status]}
                    </button>
                    <button type="button" className="dev-btn danger mini" disabled={busy} onClick={() => onDeleteTemplate(template.id)}>حذف</button>
                  </div>
                )}
                {!!revision?.amenity_details?.length && (
                  <small>{revision.amenity_details.map((item) => `${item.amenity_name}: ${formatAmenityValue({ value_type: item.amenity_value_type }, item.value)}`).join('، ')}</small>
                )}
              </div>
            );
          })}
        </div>
      </form>

      <form className="dev-panel dev-merged-card" onSubmit={onBulkCreateUnits}>
        <div className="dev-merged-head">
          <h2>ایجاد واحدها</h2>
          <button className="dev-btn primary mini" disabled={!selectedProject || !unitBatch.template_revision || !unitBatch.count || busy}>ایجاد</button>
        </div>
        <div className="dev-form-stack compact">
          <Field label="ساختار">
            <Select value={unitBatch.section} onChange={(value) => onUnitBatchChange({ ...unitBatch, section: value })}>
              <option value="">بدون بخش</option>
              {projectBundle.sections.map((section) => <option key={section.id} value={section.id}>{sectionPath(section, projectBundle.sections)}</option>)}
            </Select>
          </Field>
          <Field label="تیپ">
            <Select value={unitBatch.template_revision} onChange={(value) => onUnitBatchChange({ ...unitBatch, template_revision: value })}>
              <option value="">انتخاب تیپ</option>
              {publishedRevisions.map((revision) => <option key={revision.id} value={revision.id}>{revision.templateName}</option>)}
            </Select>
          </Field>
          <div className="dev-inline-fields">
            <Field label="طبقه"><TextInput type="number" value={unitBatch.floor} onChange={(value) => onUnitBatchChange({ ...unitBatch, floor: value })} /></Field>
            <Field label="تعداد"><TextInput type="number" value={unitBatch.count} onChange={(value) => onUnitBatchChange({ ...unitBatch, count: value })} /></Field>
          </div>
          <Field label="شماره واحدها">
            <textarea
              className="dev-textarea compact"
              value={unitBatch.unit_numbers || ''}
              onChange={(event) => onUnitBatchChange({ ...unitBatch, unit_numbers: event.target.value })}
            />
          </Field>
        </div>
        <div className="dev-unit-preview compact">
          <div>
            {(unitBatch.unit_numbers || '')
              .split(/[\n,،]+/)
              .map((item) => item.trim())
              .filter(Boolean)
              .slice(0, 18)
              .map((unit) => <span key={unit}>{unit}</span>)}
            {!unitBatch.unit_numbers?.trim() && <p className="dev-muted">بعد از وارد کردن تعداد، شماره ها خودکار ساخته می شوند.</p>}
          </div>
        </div>
        <div className="dev-unit-stats compact">
          <div><strong>{projectBundle.sections.length}</strong><span>ساختار</span></div>
          <div><strong>{publishedRevisions.length}</strong><span>تیپ منتشر</span></div>
          <div><strong>{projectBundle.units.length}</strong><span>واحد</span></div>
        </div>
        <div className="dev-list compact dev-unit-list">
          {projectBundle.units.slice(0, 18).map((unit) => (
            <div className="dev-list-row static" key={unit.id}>
              <span>واحد {unit.unit_number}</span>
              <small>{unit.floor ? `طبقه ${unit.floor}` : 'بدون طبقه'}</small>
              <button type="button" className="dev-btn danger mini" disabled={busy} onClick={() => onDeleteUnit(unit.id)}>حذف</button>
            </div>
          ))}
          {!projectBundle.units.length && <small className="dev-muted">هنوز واحدی ایجاد نشده است.</small>}
        </div>
      </form>
    </div>
  );
}
